import {ImageIcon} from '@/components/svg/ImageIcon';
import {SendIcon} from '@/components/svg/SendIcon';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {AttachmentIcon, CloseIcon} from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  DOCUMENTS_ACCEPT,
  IMAGES_ACCEPT,
  MAX_FILE_SIZE,
  formatDateForDisplay,
} from 'ff-common';
import {ChangeEvent, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ConversationClientHeader} from './ConversationClientHeader';

// TODO: Le code concernant la categorisation des nouvelles conversations par raison est pour le moment conservé mais commenté, à voir si on le réintègre ou pas en fonction des retours utilisateurs.
// Types de raison pour une nouvelle conversation
// type RaisonConversation = 'commande' | 'compte_client' | 'livraison' | 'autres';

interface ConversationClientNouvelleViewProps {
  onRetour: () => void;
  onFermer: () => void;
  onEnvoyer: (titre: string, message: string, fichiers: File[]) => void;
  isSubmitting?: boolean;
}

/**
 * Vue pour créer une nouvelle conversation côté client.
 * Le client sélectionne une raison puis tape son message.
 */
export const ConversationClientNouvelleView: React.FC<
  ConversationClientNouvelleViewProps
> = ({onRetour, onFermer, onEnvoyer, isSubmitting = false}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();

  // État pour la raison sélectionnée et le message
  // const [raisonSelectionnee, setRaisonSelectionnee] = useState<RaisonConversation | null>(null);
  // State pour le message a envoyer
  const [message, setMessage] = useState('');
  // State pour les fichiers selectionnes
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Références
  const textareaRef = useRef<HTMLTextAreaElement>(null); // Pour réinitialiser la hauteur du textarea
  const documentInputRef = useRef<HTMLInputElement>(null); // Input caché pour documents
  const imageInputRef = useRef<HTMLInputElement>(null); // Input caché pour images

  // Types de raisons disponibles
  // const raisons: {value: RaisonConversation; label: string}[] = [
  //   {value: 'commande', label: t('client.conversations.nouvelle.raison_commande')},
  //   {value: 'compte_client', label: t('client.conversations.nouvelle.raison_compte_client')},
  //   {value: 'livraison', label: t('client.conversations.nouvelle.raison_livraison')},
  //   {value: 'autres', label: t('client.conversations.nouvelle.raison_autres')},
  // ];

  // Gérer la sélection de fichiers
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const piecesJointesValides: File[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        textToast(t('global.fichier_trop_volumineux', {nom: file.name}));
      } else {
        piecesJointesValides.push(file);
      }
    }
    if (piecesJointesValides.length > 0) {
      setSelectedFiles((prev) => [...prev, ...piecesJointesValides]);
    }
    // Réinitialiser l'input pour permettre de resélectionner le même fichier
    e.target.value = '';
  };

  // Supprimer un fichier de la liste
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Titre par défaut avec la date du jour
  const titreParDefaut = t(
    'client.conversations.nouvelle.titre_conversation_date',
    {
      date: formatDateForDisplay(new Date().toISOString(), 'd MMMM yyyy'),
    },
  );

  // Gérer l'envoi du message
  const handleEnvoyer = () => {
    if (peutEnvoyer) {
      // Si aucune raison ou "autres", utiliser le titre par défaut avec la date
      // let titre: string;
      // if (!raisonSelectionnee || raisonSelectionnee === 'autres') {
      //   titre = titreParDefaut;
      // } else {
      //   titre = raisons.find(r => r.value === raisonSelectionnee)?.label ?? titreParDefaut;
      // }
      const titre: string = titreParDefaut;
      onEnvoyer(titre, message, selectedFiles);
      setMessage('');
      setSelectedFiles([]);
      if (textareaRef.current) {
        // Réinitialiser la hauteur du textarea
        textareaRef.current.style.height = '';
      }
    }
  };

  // Envoyer avec Entrée (Shift+Entrée pour nouvelle ligne)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnvoyer();
    }
  };

  // Vérifie si on peut envoyer le message (la raison est optionnelle)
  const peutEnvoyer = message.trim().length > 0;

  return (
    <Box
      display='flex'
      flexDirection='column'
      h='100%'
    >
      {/* Header */}
      <ConversationClientHeader
        titre={t('client.conversations.nouvelle.titre')}
        onRetour={onRetour}
        onFermer={onFermer}
      />

      {/* Contenu */}
      <Box
        flex={1}
        overflowY='auto'
        p={4}
        bg='gray.bg'
      >
        {/* Date du jour */}
        <Box
          bg='white'
          borderRadius='8px'
          px={3}
          py={1}
          mb={4}
          mx='auto'
          w='fit-content'
        >
          <Text
            fontSize='xs'
            color='gray.dark'
            textAlign='center'
          >
            {formatDateForDisplay(new Date().toISOString(), 'd MMMM yyyy')}
          </Text>
        </Box>

        {/* Message d'accueil */}
        {/* <Box
          bg="white"
          borderRadius="12px"
          p={4}
          mb={1}
          w="90%"
          mr="auto"
        >
          <Text fontSize="sm"
            color="black.text"
            whiteSpace="pre-line">
            {t('client.conversations.nouvelle.message_accueil')}
          </Text>
        </Box> */}

        {/* France Frais Connect */}
        {/* <HStack
          spacing={2}
          mb={9}
          w="90%"
          justifyContent='start'
        >
          <Text fontSize="sm"
            fontWeight="700"
            color="gray.dark">
            {t('client.conversations.nouvelle.france_frais_connect')}
          </Text>
        </HStack> */}

        {/* Choix de la raison */}
        {/* <VStack spacing={2}
          align="stretch"
          w='80%'>
          {raisons.map((raison) => (
            <Button
              key={raison.value}
              variant="outline"
              justifyContent="flex-start"
              borderColor={raisonSelectionnee === raison.value ? 'blue.main' : 'gray.light'}
              bg={raisonSelectionnee === raison.value ? 'blue.main' : 'white'}
              color={raisonSelectionnee === raison.value ? 'white' : 'black.text'}
              fontWeight="400"
              borderRadius="8px"
              py={6}
              _hover={{
                borderColor: 'gray.dark',
                bg: 'gray.bg',
                color: 'black.text',
              }}
              onClick={() => {setRaisonSelectionnee(raison.value);}}
            >
              {raison.label}
            </Button>
          ))}
        </VStack> */}
      </Box>

      {/* Zone de saisie du message */}
      <Box
        p={4}
        pb={{base: '5rem', md: 4}}
        bg='white'
        borderBottomRadius={{base: 0, md: '12px'}}
        borderTop='1px solid'
        borderColor='gray.light'
      >
        {/* Inputs cachés pour sélection de fichiers */}
        <input
          type='file'
          ref={documentInputRef}
          onChange={handleFileSelect}
          accept={DOCUMENTS_ACCEPT}
          multiple // Permet la sélection de plusieurs fichiers à la fois
          style={{display: 'none'}}
        />
        <input
          type='file'
          ref={imageInputRef}
          onChange={handleFileSelect}
          accept={IMAGES_ACCEPT}
          multiple
          style={{display: 'none'}}
        />

        {/* Aperçu des fichiers sélectionnés */}
        {selectedFiles.map((file, index) => (
          <HStack
            key={`${file.name}-${index}`}
            bg='gray.bg'
            borderRadius='8px'
            p={2}
            mb={2}
            justify='space-between'
          >
            <VStack
              align='start'
              spacing={0}
              flex={1}
              minW={0}
            >
              <Text
                fontSize='sm'
                color='gray.dark'
                noOfLines={1}
              >
                {file.name}
              </Text>
              <Text
                fontSize='xs'
                color='gray.500'
              >
                {t('client.conversations.taille_fichier_ko', {
                  taille: (file.size / 1024).toFixed(1),
                })}
              </Text>
            </VStack>
            <IconButton
              aria-label='X'
              icon={<CloseIcon boxSize={3} />}
              size='sm'
              variant='ghost'
              onClick={() => {
                handleRemoveFile(index);
              }}
            />
          </HStack>
        ))}

        {/* Zone de saisie du message */}
        <Textarea
          placeholder={t('client.conversations.nouvelle.placeholder_message')}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          fontSize='sm'
          bg='transparent'
          border='none'
          resize='none'
          minH='40px'
          maxH='150px'
          height='auto'
          rows={1}
          overflow='auto'
          _focus={{border: 'none', boxShadow: 'none'}}
          ref={textareaRef}
          isDisabled={isSubmitting}
          _disabled={{
            cursor: 'not-allowed',
            opacity: 0.6,
          }}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${target.scrollHeight}px`;
          }}
        />
        {/* Boutons pièces jointes + envoyer */}
        <HStack
          ml={1}
          spacing={1}
          justify='space-between'
        >
          <HStack spacing={1}>
            <Tooltip
              label={t('client.conversations.ajouter_piece_jointe')}
              hasArrow
            >
              <IconButton
                aria-label={t('client.conversations.ajouter_piece_jointe')}
                icon={<AttachmentIcon />}
                variant='ghost'
                color='gray.dark'
                onClick={() => {
                  documentInputRef.current?.click();
                }}
                isDisabled={isSubmitting}
                boxSize='40px'
                minW='40px'
              />
            </Tooltip>
            <Tooltip
              label={t('client.conversations.ajouter_image')}
              hasArrow
            >
              <IconButton
                aria-label={t('client.conversations.ajouter_image')}
                icon={<ImageIcon />}
                variant='ghost'
                color='gray.dark'
                onClick={() => {
                  imageInputRef.current?.click();
                }}
                isDisabled={isSubmitting}
                boxSize='40px'
                minW='40px'
              />
            </Tooltip>
          </HStack>
          <IconButton
            aria-label={t('client.conversations.nouvelle.envoyer')}
            icon={<SendIcon />}
            colorScheme='blue'
            bg='blue.main'
            color='white'
            onClick={handleEnvoyer}
            isLoading={isSubmitting}
            isDisabled={!peutEnvoyer || isSubmitting}
            _hover={{bg: peutEnvoyer ? 'blue.dark' : 'gray.light'}}
            _disabled={{bg: 'gray.light', cursor: 'not-allowed'}}
            boxSize='40px'
            minW='40px'
            borderRadius='8px'
          />
        </HStack>
      </Box>
    </Box>
  );
};
